import { Card, Form, Button } from 'react-bootstrap';
import {
  ConditionGroup,
  Condition,
  isGroup,
} from '@/modules/trade-rule-builder/model/trade-rule-builder';
import ConditionEditor from './condition-editor';
import type { SeriesOption } from '@/modules/trade-rule-builder/model/trade-rule-builder';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface Props {
  group: ConditionGroup;
  ruleId: string;
  onUpdateGroupOperator: (groupId: string, value: 'AND' | 'OR') => void;
  onUpdateCondition: <K extends keyof Condition>(
    groupId: string,
    conditionId: string,
    key: K,
    value: Condition[K],
  ) => void;
  onAddCondition: (groupId: string) => void;
  onAddGroup: (groupId: string) => void;
  onDeleteCondition?: (groupId: string, conditionId: string) => void;
  onDeleteGroup?: (groupId: string) => void;
  seriesOptions?: SeriesOption[];
}

export default function ConditionGroupEditor({
  group,
  ruleId,
  onUpdateGroupOperator,
  onUpdateCondition,
  onAddCondition,
  onAddGroup,
  onDeleteCondition,
  onDeleteGroup,
  seriesOptions,
}: Props) {
  const showOperator = group.children.length > 1;

  return (
    <Card className="mb-2 p-2" style={{ border: '1px solid #5c5c5cff' }}>
      {showOperator && (
        <Form.Select
          value={group.operator}
          onChange={e => onUpdateGroupOperator(group.id, e.target.value as 'AND' | 'OR')}
          size="sm"
          className="mb-2"
        >
          <option value="AND">AND</option>
          <option value="OR">OR</option>
        </Form.Select>
      )}
      {group.children.map(child => (
        <div key={child.id} className="d-flex align-items-start">
          <div className="flex-grow-1">
            {isGroup(child) ? (
              <ConditionGroupEditor
                group={child}
                ruleId={ruleId}
                onUpdateGroupOperator={onUpdateGroupOperator}
                onUpdateCondition={onUpdateCondition}
                onAddCondition={onAddCondition}
                onAddGroup={onAddGroup}
                onDeleteCondition={onDeleteCondition}
                onDeleteGroup={onDeleteGroup}
                seriesOptions={seriesOptions}
              />
            ) : (
              <ConditionEditor
                condition={child}
                onUpdate={(key, value) => onUpdateCondition(group.id, child.id, key, value)}
                onDelete={
                  onDeleteCondition ? () => onDeleteCondition(group.id, child.id) : undefined
                }
                seriesOptions={seriesOptions}
              />
            )}
          </div>
          {isGroup(child) && (
            <div className="ms-2 mt-1">
              <FontAwesomeIcon
                icon={faCircleXmark}
                style={{ cursor: 'pointer' }}
                onMouseDown={() => onDeleteGroup?.(child.id)}
              />
            </div>
          )}
        </div>
      ))}
      <div className="d-flex gap-2">
        <Button
          size="sm"
          onMouseUp={e => {
            onAddCondition(group.id);
          }}
        >
          + Condition
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onMouseUp={e => {
            onAddGroup(group.id);
          }}
        >
          + Group
        </Button>
      </div>
    </Card>
  );
}
